import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionTemplate, useSpring } from 'framer-motion';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';
import ResponsiveImage from './ResponsiveImage';
import { buildLooksSrcSet, FULL_BLEED_SIZES } from '../../lib/responsive';

/**
 * Hero V9 — Awwwards 2025 Scroll-Reactive Text-Mask.
 *
 * Signature effect:
 *  - Section is 220vh tall; visual layer is `sticky top-0 h-screen`.
 *  - As you scroll, the MODARIA title (with the photo clipped through it)
 *    SCALES UP from 1 → 2.4, while the background image inside the letters
 *    PARALLAX-slides (background-position-y from 50% → 90%).
 *  - The full-bleed backdrop simultaneously fades and zooms inversely,
 *    so attention is funneled into the text-mask cinemagraph.
 *  - Photos rotate every 5.2s; words rotate every 2.4s.
 *  - When you reach the bottom of the section, the title fades and the next
 *    section (Manifesto) appears beneath naturally.
 */

const ROTATING_WORDS_BY_LOCALE = {
  es: ['VALIDADA', 'GOBERNADA', 'ESCALADA', 'INTEGRADA', 'AUDITADA', 'TUYA'],
  en: ['VALIDATED', 'GOVERNED', 'SCALED', 'INTEGRATED', 'AUDITED', 'YOURS'],
};

const _API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
// Para los fallback (looks/after/<id>) construimos un objeto {src, srcSet}
// que aprovecha las variantes existentes -thumb (500w) / -medium (1000w) / -full (1600w).
const _u = (id) => ({
  src: `${_API_BASE}/uploads/looks/after/${id}-full.webp`,
  srcSet: buildLooksSrcSet(`/uploads/looks/after/${id}`),
});

// Real MODARIA BR (after) renders — full set of 13 looks for max variety in hero rotation.
const FALLBACK_PHOTOS = [
  _u('01'), _u('05'), _u('11'), _u('03'),
  _u('07'), _u('12'), _u('02'), _u('09'),
  _u('06'), _u('13'), _u('04'), _u('10'),
  _u('08'),
];

export default function IntroHero() {
  const { content, locale } = useContent();
  const data = content.intro_hero || {};
  const heroRef = useRef(null);

  const [photoIdx, setPhotoIdx] = useState(0);

  // Photo carousel — priority:
  //   1) Assets tagged 'intro_hero.carousel' in the Media Library (admin-curated).
  //   2) content.showcase.gallery (CMS).
  //   3) FALLBACK_PHOTOS (compiled-in defaults).
  const [mediaCarousel, setMediaCarousel] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${_API_BASE}/api/media/by-section/intro_hero.carousel?limit=12`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        // Mantén el objeto completo (incluye variants + variants_avif) para alimentar srcset/sizes.
        const items = (d.items || [])
          .filter((a) => a.url || a.thumb_url)
          .map((a) => {
            const main = a.url || a.thumb_url;
            const src = main.startsWith('http') ? main : `${_API_BASE}${main}`;
            return { src, variants: a.variants, variantsAvif: a.variants_avif };
          });
        setMediaCarousel(items);
      })
      .catch((e) => { console.warn('intro_hero.carousel fetch failed', e); });
    return () => { cancelled = true; };
  }, []);

  const photos = (() => {
    if (mediaCarousel.length >= 3) return mediaCarousel.slice(0, 12);
    const gallery = content?.showcase?.gallery;
    if (Array.isArray(gallery) && gallery.length >= 6) {
      return gallery.slice(0, 12).map((u) => ({ src: u }));
    }
    if (data.background_image) return [{ src: data.background_image }, ...FALLBACK_PHOTOS];
    return FALLBACK_PHOTOS;
  })();

  // Keep photoIdx within range if the source array shrinks (e.g. when
  // the by-section fetch resolves and replaces the longer fallback set).
  useEffect(() => {
    setPhotoIdx((idx) => (idx >= photos.length ? 0 : idx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  useEffect(() => {
    const id = setInterval(() => setPhotoIdx((i) => (i + 1) % photos.length), 5200);
    return () => clearInterval(id);
  }, [photos.length]);

  // Rotating word
  const words = ROTATING_WORDS_BY_LOCALE[locale] || ROTATING_WORDS_BY_LOCALE.es;
  const [wordIdx, setWordIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setWordIdx((i) => (i + 1) % words.length), 2400);
    return () => clearInterval(id);
  }, [words.length]);

  // === Scroll-driven cinema (the signature) ===
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end end'],
  });

  // Spring-smoothed progress for buttery interpolation
  const smooth = useSpring(scrollYProgress, { stiffness: 80, damping: 22, mass: 0.4 });

  // Title grows from 1 → 2.4 across the scroll, then dissolves at the very end.
  const titleScale = useTransform(smooth, [0, 0.65, 1], [1, 1.85, 2.4]);
  const titleOpacity = useTransform(smooth, [0, 0.78, 0.95, 1], [1, 1, 0.6, 0]);
  const titleBlur = useTransform(smooth, [0, 0.85, 1], [0, 0, 6]);
  const titleFilter = useMotionTemplate`blur(${titleBlur}px) contrast(1.05) saturate(1.05)`;

  // Background-position-y inside the text-mask: photo slides 50% → 95% as you scroll.
  // This is the parallax-inside-text effect that signature Awwwards sites use.
  const bgPosY = useTransform(smooth, [0, 1], [50, 95]);
  const bgPosTemplate = useMotionTemplate`50% ${bgPosY}%`;

  // Full-bleed backdrop: fades and zooms inversely so the text-mask becomes hero.
  const photoScale = useTransform(smooth, [0, 1], [1, 1.18]);
  const photoOpacity = useTransform(smooth, [0, 0.6, 1], [1, 0.35, 0.08]);
  const photoY = useTransform(smooth, [0, 1], ['0%', '-22%']);

  // CTAs and rotator fade out as you scroll past 30%
  const uiOpacity = useTransform(smooth, [0, 0.25, 0.4], [1, 0.6, 0]);
  const uiY = useTransform(smooth, [0, 0.4], ['0%', '-30%']);

  // Corner labels fade and translate with scroll
  const cornersOpacity = useTransform(smooth, [0, 0.5, 0.8], [1, 0.4, 0]);

  const scrollTo = (target) => {
    if (!target) return;
    document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
  };

  const marquee = pickLocale(data.marquee_text, locale, 'AI FASHION OPS · SINCE 2024');
  const productionLabel = locale === 'es' ? 'PRODUCCIÓN' : 'PRODUCTION';
  const today = new Date();
  const dateLabel = today.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase();

  return (
    <section
      ref={heroRef}
      id="intro"
      data-testid="intro-hero"
      className="relative w-full bg-bg"
      style={{ minHeight: '220vh' }}
    >
      {/* React 19 hoists <link rel="preload"> to <head> automáticamente.
          Indica al navegador que descargue YA la primera foto del carousel
          con el srcset completo · mejora drásticamente el LCP. */}
      {photos[0]?.src && (
        <link
          rel="preload"
          as="image"
          href={photos[0].src}
          imageSrcSet={
            photos[0].srcSet ||
            (photos[0].variants
              ? Object.entries(photos[0].variants)
                  .map(([w, u]) => `${u.startsWith('http') ? u : `${_API_BASE}${u}`} ${w}w`)
                  .join(', ')
              : undefined)
          }
          imageSizes="100vw"
          fetchPriority="high"
        />
      )}
      {/* Sticky stage — pinned for the entire 220vh of scroll */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">

        {/* === Layer 1 — Full-bleed photo carousel (parallax + scale + fade) === */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{ scale: photoScale, opacity: photoOpacity, y: photoY }}
        >
          <AnimatePresence>
            <motion.div
              key={photoIdx}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.4, ease: [0.45, 0, 0.2, 1] },
                scale: { duration: 6, ease: 'linear' },
              }}
              className="absolute inset-0"
            >
              {/* Letterbox blur background: fills the side bars with a soft, blurred copy
                  of the same photo, so on big screens (xl+) the image is fully visible
                  via object-contain WITHOUT ugly hard black bars. Standard cinema pattern
                  used by Apple/Netflix/Vimeo for portrait content on landscape viewports. */}
              <ResponsiveImage
                src={photos[photoIdx].src}
                variants={photos[photoIdx].variants}
                variantsAvif={photos[photoIdx].variantsAvif}
                srcSet={photos[photoIdx].srcSet}
                sizes={FULL_BLEED_SIZES}
                alt=""
                aria-hidden
                loading={photoIdx === 0 ? 'eager' : 'lazy'}
                fetchPriority={photoIdx === 0 ? 'high' : undefined}
                className="hidden xl:block absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-60"
              />
              <ResponsiveImage
                src={photos[photoIdx].src}
                variants={photos[photoIdx].variants}
                variantsAvif={photos[photoIdx].variantsAvif}
                srcSet={photos[photoIdx].srcSet}
                sizes={FULL_BLEED_SIZES}
                alt=""
                loading={photoIdx === 0 ? 'eager' : 'lazy'}
                fetchPriority={photoIdx === 0 ? 'high' : undefined}
                className="relative w-full h-full object-cover xl:object-contain xl:object-center"
                style={{ objectPosition: 'center 30%' }}
                data-testid={`intro-photo-${photoIdx}`}
              />
              {/* Subtle bottom fade so the marquee stays legible (was: from-bg via-bg/30 to-bg/10 — too dark, cropped face on big screens) */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-bg/85 via-bg/30 to-transparent" />
              {/* Soft side wash for editorial weight (kept light) */}
              <div className="absolute inset-0 bg-gradient-to-r from-bg/15 via-transparent to-bg/10" />
              {/* Vignette only at the corners, not over the face */}
              <div
                className="absolute inset-0"
                style={{ background: 'radial-gradient(ellipse at center, transparent 65%, rgba(5,5,5,0.35) 100%)' }}
              />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* === Layer 2 — Editorial corner labels (fade with scroll) === */}
        <motion.div
          className="absolute top-24 md:top-28 left-0 right-0 z-20 flex items-start justify-between px-5 md:px-10 gap-4 pointer-events-none"
          style={{ opacity: cornersOpacity }}
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-[10px] md:text-[11px] font-mono uppercase tracking-[0.28em] text-ink-muted"
          >
            <div className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-acid animate-pulse" />
              {pickLocale(data.eyebrow_brand, locale)}
            </div>
            <div className="mt-2 text-ink-dim">{dateLabel} · GIRONA · BARCELONA · MADRID</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="text-right text-[10px] md:text-[11px] font-mono uppercase tracking-[0.28em] text-ink-muted"
          >
            <div>ISSUE Nº 26</div>
            <div className="mt-2 text-ink-dim">{pickLocale(data.next_value, locale)}</div>
          </motion.div>
        </motion.div>

        {/* === Layer 3 — Center: MODARIA scaling + parallax-mask cinemagraph === */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-5 pointer-events-none">
          {/* The hero killer move: scale + bg-position-y parallax inside the letters.
              `background-size: contain` so the FULL editorial photo is visible inside
              the letters (no aggressive cropping). */}
          <motion.h1
            key={`title-${photoIdx}`}
            initial={{ opacity: 0, y: 40, letterSpacing: '0.02em' }}
            animate={{ opacity: 1, y: 0, letterSpacing: '-0.005em' }}
            transition={{ duration: 1, delay: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="text-block leading-[0.86] text-center"
            style={{
              fontSize: 'clamp(5rem, 19vw, 22rem)',
              backgroundImage: `url(${photos[photoIdx]})`,
              backgroundSize: 'cover',
              backgroundPosition: bgPosTemplate,
              backgroundRepeat: 'no-repeat',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              transition: 'background-image 1.4s ease',
              textShadow: '0 0 60px rgba(232,255,58,0.15)',
              scale: titleScale,
              opacity: titleOpacity,
              filter: titleFilter,
              willChange: 'transform, opacity, background-position',
              transformOrigin: 'center center',
            }}
            data-testid="intro-title"
          >
            {data.title_top || 'MODARIA'}
          </motion.h1>

          {/* UI block (rotator + subtitle + CTAs) — fades out as you scroll */}
          <motion.div
            className="mt-8 md:mt-10 flex flex-col items-center"
            style={{ opacity: uiOpacity, y: uiY }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.0 }}
              className="flex items-baseline gap-3 md:gap-5 select-none"
              data-testid="intro-rotator"
            >
              <span className="font-mono text-[11px] md:text-sm uppercase tracking-[0.28em] text-ink-muted">
                {productionLabel}
              </span>
              <span
                className="relative inline-flex items-baseline"
                style={{ minWidth: '6.5ch' }}
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={words[wordIdx]}
                    initial={{ opacity: 0, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(8px)' }}
                    transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                    className="font-display italic text-acid text-2xl md:text-4xl lg:text-5xl leading-none"
                    data-testid="intro-rotator-word"
                  >
                    {words[wordIdx]}.
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.3 }}
              className="mt-6 md:mt-8 text-[11px] md:text-sm font-mono uppercase tracking-[0.22em] text-ink-muted text-center max-w-md"
              data-testid="intro-subtitle"
            >
              {pickLocale(data.subtitle, locale)}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.5 }}
              className="mt-8 flex items-center gap-3 pointer-events-auto"
            >
              <button
                onClick={() => scrollTo('#contact')}
                data-testid="intro-cta-primary"
                className="btn-acid magnetic !text-[11px]"
              >
                {pickLocale(data.cta_primary, locale)} <ArrowRight size={13} />
              </button>
              <button
                onClick={() => scrollTo('#features')}
                data-testid="intro-cta-secondary"
                className="btn-ghost magnetic !text-[11px]"
              >
                {pickLocale(data.cta_secondary, locale)}
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* === Layer 4 — Carousel position indicator === */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.6 }}
          style={{ opacity: cornersOpacity }}
          className="absolute bottom-32 md:bottom-40 right-5 md:right-10 z-20 flex flex-col items-end gap-2"
          data-testid="intro-photo-indicator"
        >
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => setPhotoIdx(i)}
              className="flex items-center gap-3 group magnetic"
              aria-label={`Photo ${i + 1}`}
              data-testid={`intro-photo-dot-${i}`}
            >
              <span className={`text-[10px] font-mono uppercase tracking-[0.22em] transition-colors ${i === photoIdx ? 'text-acid' : 'text-ink-dim group-hover:text-ink-muted'}`}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className={`block h-px transition-all duration-700 ${i === photoIdx ? 'w-12 bg-acid' : 'w-6 bg-ink-dim group-hover:w-9 group-hover:bg-ink-muted'}`} />
            </button>
          ))}
        </motion.div>

        {/* === Marquee strip === */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.8 }}
          style={{ opacity: cornersOpacity }}
          className="absolute bottom-0 inset-x-0 z-10 overflow-hidden border-t border-line py-3 bg-bg/40 backdrop-blur-sm"
          data-testid="intro-marquee"
        >
          <div className="km-scroll">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="text-block block-medium text-ink-muted hover:text-acid transition-colors whitespace-nowrap flex items-center gap-6">
                {marquee} <span className="text-acid">●</span>
              </span>
            ))}
          </div>
        </motion.div>

        {/* === Scroll indicator === */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 8, 0] }}
          transition={{ delay: 1.4, duration: 2, repeat: Infinity }}
          style={{ opacity: cornersOpacity }}
          className="absolute left-5 md:left-10 bottom-32 md:bottom-40 z-20 hidden md:flex flex-col items-start gap-2"
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-ink-muted" style={{ writingMode: 'vertical-rl' }}>
            {pickLocale(data.scroll_label, locale)}
          </span>
          <ArrowDown size={14} className="text-acid" />
        </motion.div>
      </div>
    </section>
  );
}
